import { Component, ErrorInfo, ReactNode } from 'react'

import ErrorPage from '@/app/error'
import { appSettings } from '@/shared/consts/appSettings'

const MAX_CHUNK_RELOAD_ATTEMPTS = 3
const CHUNK_RELOAD_QUERY_KEY = 'r'
const CHUNK_RELOAD_TIMESTAMP_KEY = 't'

function isChunkLoadError(error: Error): boolean {
  const message = error?.message ?? ''
  return (
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('importing a module script failed')
  )
}

function isChatScriptError(error: Error): boolean {
  const message = error?.message ?? ''
  const stack = error?.stack ?? ''
  const full = `${message} ${stack}`.toLowerCase()
  return (
    full.includes('be-frame') ||
    full.includes('niceincontact') ||
    full.includes('chat.js') ||
    (full.includes('window[') && full.includes('is not a function'))
  )
}

function reloadWithRetryQuery(): boolean {
  const params = new URLSearchParams(window.location.search)
  const current = parseInt(params.get(CHUNK_RELOAD_QUERY_KEY) ?? '0', 10)

  if (current >= MAX_CHUNK_RELOAD_ATTEMPTS) return false

  params.set(CHUNK_RELOAD_QUERY_KEY, String(current + 1))
  params.set(CHUNK_RELOAD_TIMESTAMP_KEY, String(Date.now()))
  const search = params.toString()
  const hash = window.location.hash
  const newUrl = `${appSettings.publicUrl}?${search}${hash}`
  window.location.replace(newUrl)

  return true
}

interface Props {
  children: ReactNode
  variant?: 'page' | 'global'
}

interface State {
  error: Error | null
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { error: null }
  }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary capturou erro:', error, errorInfo)

    if (isChunkLoadError(error)) {
      const isReloaded = reloadWithRetryQuery()
      if (isReloaded) return
    }
  }

  reset = () => {
    this.setState({ error: null })
  }

  render() {
    const { error } = this.state

    if (error) {
      if (isChatScriptError(error)) {
        console.error('isChatScriptError', error)
        return this.props.children
      }

      return <ErrorPage error={error} reset={this.reset} />
    }

    return this.props.children
  }
}
